import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import api from '../../api';

const ReviewsManagement = ({ userId }) => {
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReviews();
  }, [userId]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/creators/${userId}/reviews`);
      const data = response.data;

      setReviews(data.reviews || []);
      setAverageRating(data.averageRating || 0);
      setTotalReviews(data.totalReviews || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-200 text-gray-200'
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900">Reviews</h2>
        <p className="text-gray-600 mt-1">See what brands are saying about your work</p>
      </div>

      <div className="p-6">
        {/* Overall Rating Summary */}
        {totalReviews > 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-6">
            <div className="flex flex-col md:flex-row items-center justify-center md:justify-start gap-6">
              {/* Average Rating */}
              <div className="text-center md:text-left">
                <div className="text-5xl font-bold text-gray-900">
                  {averageRating.toFixed(1)}
                </div>
                <div className="flex items-center justify-center md:justify-start mt-2">
                  {renderStars(Math.round(averageRating))}
                </div>
                <div className="text-sm text-gray-600 mt-2">
                  Based on {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
                </div>
              </div>

              {/* Rating Distribution (Placeholder for future) */}
              <div className="flex-1 ml-0 md:ml-8">
                <div className="text-sm text-gray-600">
                  Your average rating reflects the quality of your work and professionalism
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reviews List */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {reviews.length === 0 && !error && (
          <div className="bg-gray-50 rounded-lg p-12 text-center">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No reviews yet</h3>
            <p className="text-gray-600">
              Complete campaigns and deliver great work to receive reviews from brands
            </p>
          </div>
        )}

        {reviews.length > 0 && (
          <div className="space-y-4">
            {reviews.map((review) => (
              <div
                key={review.id}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                {/* Review Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    {/* Brand Logo */}
                    {review.brandLogoUrl && (
                      <img
                        src={review.brandLogoUrl}
                        alt={review.brandName}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      />
                    )}
                    {!review.brandLogoUrl && (
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-semibold text-lg">
                          {review.brandName?.charAt(0) || 'B'}
                        </span>
                      </div>
                    )}

                    {/* Brand & Campaign Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {review.brandName || 'Unknown Brand'}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Campaign: <span className="font-medium">{review.campaignTitle || 'N/A'}</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(review.createdAt)}
                      </p>
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="flex flex-col items-end gap-2">
                    {renderStars(review.rating)}
                    <span className="text-sm font-medium text-gray-700">
                      {review.rating}.0 / 5.0
                    </span>
                  </div>
                </div>

                {/* Review Text */}
                {review.reviewText && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-700 leading-relaxed">
                      "{review.reviewText}"
                    </p>
                  </div>
                )}

                {!review.reviewText && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-500 italic text-sm">
                      No additional comments provided
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Tips for Getting Better Reviews */}
        {totalReviews > 0 && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">
              💡 Tips for Getting Better Reviews
            </h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Deliver high-quality content that meets campaign requirements</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Communicate proactively with brands throughout the campaign</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Meet deadlines and be professional in all interactions</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Go above and beyond to exceed brand expectations</span>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsManagement;


